import asyncEachSeries from 'async/eachSeries';
import asyncSeries from 'async/series';
import EventEmitter from 'events';
import React from 'react';

import MediaStore from '../stores/media_store';

import { AsyncIterator } from '../tools/async_iterator';
import { closeStream, copyFile, createWritableStream } from '../tools/file';
import util from '../tools/util';

export type StatusValue =
  | 'waiting'
  | 'get_url'
  | 'start_download'
  | 'downloading'
  | 'copying'
  | 'done'
  | 'error';
export type Item = {
  org_id?: number;
  media_grant_id?: number;
  media_id?: number;
  media_path: string;
  file_size: number;
  last_err: null | string;
  fatal_err: null | string;
  progress: number;
  is_done: boolean;
  status: StatusValue;
  position: number;
  src_path?: string;
  skip?: boolean;
  stream?: WritableStream;
  abort?: AbortController;
};
export default {
  useList,
  useSummary,
  run,
};

const ITER_LIMIT = 4;
const ERROR_LIMIT = 5;
const RETRY_INTERVAL = 10 * 1000;

let g_rootDir;
let g_outputList: Item[] = [];
let g_list: Item[] = [];
let g_summary;
let g_isRunning = false;
const g_eventEmitter = new EventEmitter();
const CHANGE_EVENT = 'change';

function _emit(...args) {
  g_eventEmitter.emit(CHANGE_EVENT, ...args);
}
function _subscribe(callback: any) {
  g_eventEmitter.on(CHANGE_EVENT, callback);
  return () => {
    g_eventEmitter.removeListener(CHANGE_EVENT, callback);
  };
}
function _getList() {
  return g_outputList;
}
function _getSummary() {
  return g_summary;
}
export function useList(): Item[] {
  return React.useSyncExternalStore(_subscribe, _getList);
}
export function useSummary() {
  return React.useSyncExternalStore(_subscribe, _getSummary);
}
function _recalcList() {
  const new_list = g_list.map((item) => ({
    media_path: item.media_path,
    file_size: item.file_size,
    status: item.status,
    position: item.position,
    progress: item.progress,
    last_err: item.last_err,
    fatal_err: item.fatal_err,
    is_done: item.is_done,
    skip: item.skip,
  }));
  if (!util.deepEqual(new_list, g_outputList)) {
    g_outputList = new_list;
    _emit('upload_list');
  }
}
function _recalcSummary() {
  const summary = {
    is_running: g_isRunning,
    file_count: g_list?.length ?? 0,
    total_size: g_list?.reduce?.((m, i) => m + i.file_size, 0) ?? 0,
    success_count: g_list?.reduce?.((m, i) => m + (i.is_done ? 1 : 0), 0) ?? 0,
    success_size:
      g_list?.reduce?.((m, i) => m + (i.is_done ? i.file_size : 0), 0) ?? 0,
    fail_count: g_list?.reduce?.((m, i) => m + (i.fatal_err ? 1 : 0), 0) ?? 0,
    fail_size:
      g_list?.reduce?.((m, i) => m + (i.fatal_err ? i.file_size : 0), 0) ?? 0,
    skip_count: g_list?.reduce?.((m, i) => m + (i.skip ? 1 : 0), 0) ?? 0,
    skip_size:
      g_list?.reduce?.((m, i) => m + (i.skip ? i.file_size : 0), 0) ?? 0,
  };
  if (!util.deepEqual(summary, g_summary)) {
    g_summary = summary;
    _emit('summary');
  }
}
export function run(params, done) {
  const prefix_len = params.path.length;
  const func = (window as any).showDirectoryPicker;

  if (g_isRunning) {
    done('already_running');
  } else if (!func) {
    done('not_supported');
  } else {
    g_isRunning = true;
    g_list = [];
    _recalcList();
    _recalcSummary();

    const copy_list: any[] = [];
    asyncSeries(
      [
        (done) => {
          func({ mode: 'readwrite', startIn: 'downloads' }).then((result) => {
            g_rootDir = result;
            done();
          }, done);
        },
        (done) => {
          const file_list: any[] = [];
          const map = new Map<string, string>();

          params.list.forEach((value) => {
            const item: Item = {
              org_id: params.org_id,
              media_grant_id: params.media_grant_id,
              media_id: value.media_id,
              media_path: value.media_path.slice(prefix_len),
              file_size: value.file_size,
              status: 'waiting',
              position: 0,
              progress: 0,
              last_err: null,
              fatal_err: null,
              is_done: false,
              src_path: '',
            };
            if (map.has(value.file_hash)) {
              item.src_path = map.get(value.file_hash) ?? '';
              copy_list.push(item);
            } else {
              map.set(value.file_hash, item.media_path);
              file_list.push(item);
            }
            g_list.push(item);
          });
          _recalcList();
          _recalcSummary();

          const opts = {
            list: file_list,
            limit: ITER_LIMIT,
            times: ERROR_LIMIT,
            interval: RETRY_INTERVAL,
            iterator: _downloadHash,
            errorFilter: _errorFilter,
          };
          const iter = new AsyncIterator(opts);
          iter.on('item_final_error', (err, item) => {
            _itemStatus(item, 'error', err, err);
            _recalcSummary();
          });
          iter.on('done', (err) => {
            window.removeEventListener('beforeunload', _onBeforeUnload);
            _recalcSummary();
            _emit('done', err);
            done();
          });
          iter.start();
          window.addEventListener('beforeunload', _onBeforeUnload);
        },
        (done) => {
          asyncEachSeries(
            copy_list,
            (item, done) => {
              const { media_path, src_path } = item;
              _itemStatus(item, 'copying');
              const opts = { root: g_rootDir, src: src_path, dest: media_path };
              copyFile(opts, (err) => {
                if (err) {
                  util.errorLog('DownloadHelper._downloadHash: copy err:', err);
                }
                _itemStatus(item, err ? 'error' : 'done', err, err);
                _recalcSummary();
                done();
              });
            },
            done
          );
        },
      ],
      (err) => {
        g_isRunning = false;
        _recalcList();
        _recalcSummary();
        done(err);
      }
    );
  }
}
function _downloadHash(item, done) {
  const { org_id, media_grant_id, media_id, media_path, file_size } = item;

  let stream;
  let file;
  let url;
  asyncSeries(
    [
      (done) => {
        if (file_size > 0) {
          _itemStatus(item, 'get_url');
          const opts = { org_id, media_grant_id, media_id, inline: false };
          MediaStore.getUrl(opts, (err, new_url) => {
            if (err === 404) {
              err = 'url_not_found';
            } else if (err) {
              util.errorLog('DownloadHelper._writeMedia: url err:', err);
            }
            url = new_url;
            done(err);
          });
        } else {
          done();
        }
      },
      (done) => {
        const opts = { root: g_rootDir, path: media_path, resume: true };
        createWritableStream(opts, (err, new_stream, new_file) => {
          if (err) {
            util.errorLog('DownloadHelper._downloadHash: stream err:', err);
          }
          stream = new_stream;
          file = new_file;
          item.stream = stream;
          if (file_size > 0 && file.size >= file_size) {
            item.skip = true;
          }
          done(err);
        });
      },
      (done) => {
        if (!item.skip && file_size > 0) {
          _writeMedia({ url, item, stream, file }, (err) => {
            if (err) {
              util.errorLog('DownloadHelper._downloadHash: write err:', err);
              closeStream(stream);
            }
            done(err);
          });
        } else {
          closeStream(stream, (err) => {
            if (err) {
              util.errorLog('DownloadHelper._downloadHash: close err:', err);
            }
            done(err);
          });
        }
      },
    ],
    (err) => {
      delete item.stream;
      _itemStatus(item, err ? 'error' : 'done', err);
      _recalcSummary();
      done(err);
    }
  );
}
function _writeMedia(params, done) {
  const { url, item, stream, file } = params;
  let response;
  asyncSeries(
    [
      (done) => {
        _itemStatus(item, 'start_download');
        const temp_url = url.replace(
          'lax-proxy-03.storagesvc.sohonet.com',
          'lax-proxy-03.ares-dev.com'
        );
        const opts: any = {
          credentials: 'omit',
        };
        item.position = file.size;
        if (file.size > 0) {
          opts.headers = {
            Range: `bytes=${file.size}-`,
          };
        }
        window.fetch(temp_url, opts).then(
          (new_response) => {
            response = new_response;
            item.response = response;
            done();
          },
          (err) => {
            util.errorLog('DownloadHelper._writeMedia: fetch err:', err);
            done(err);
          }
        );
      },
      (done) => {
        _itemStatus(item, 'downloading');
        item.abort = new AbortController();
        const opts = { preventAbort: true, signal: item.abort.signal };
        const transform = new TransformStream({
          start: () => {},
          transform: (chunk, controller) => {
            item.position += chunk.byteLength;
            item.progress = item.position / item.file_size;
            _recalcList();
            controller.enqueue(chunk);
          },
        });
        response.body
          .pipeThrough(transform)
          .pipeTo(stream, opts)
          .then(
            () => done(),
            (err) => {
              util.errorLog('DownloadHelper._writeMedia: pipe err:', err);
              done(err);
            }
          )
          .catch((err) => {
            util.errorLog('DownloadHelper._writeMedia: catch err::', err);
            done(err);
          });
      },
    ],
    (err) => {
      delete item.abort;
      done(err);
    }
  );
}
function _onBeforeUnload() {
  g_list?.forEach?.((item) => {
    if (typeof item.abort?.abort === 'function') {
      item.abort.abort();
    }
  });
}
function _errorFilter(err, item) {
  let ret = true;
  if (err === 'url_not_found') {
    util.errorLog('DownloadHelper._errorFilter: stop:', err, item.media_path);
    item.fatal_err = err;
    ret = false;
  }
  return ret;
}
function _itemStatus(item, status: StatusValue, err?: any, fatal_err?: any) {
  item.status = status;
  item.last_err = err || null;
  if (fatal_err !== undefined) {
    item.fatal_err = fatal_err || null;
  }
  if (status === 'done') {
    item.is_done = true;
  }
  _recalcList();
}
